import React from "react";
import styled from "styled-components";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import PromptRibbon from "../components/Ribbon";
import aboutPhoto from "../assets/images/abhishek.jpg";

/* ===== Refined look & feel ===== */
const brand = {
  primary: "#3dccc7",
  primaryDark: "#2a5a7a",
  accent: "#ff914d",
  ink: "#0b1320",
  body: "#1f2937",
  sub: "#4b5563",
  border: "#e5edf4",
  card: "#ffffff",
  wash: "#f7f8fa",
};

const PageShell = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100dvh;
  background: #fff;
`;

const Main = styled.main`
  flex: 1 0 auto;
  padding-top: 80px;
  @media (max-width: 768px) {
    padding-top: 70px;
  }
  @media (max-width: 480px) {
    padding-top: 60px;
  }
`;

const Band = styled.div`
  background: linear-gradient(
    135deg,
    ${brand.primary} 0%,
    ${brand.primaryDark} 100%
  );
  height: 8px;
`;

const Wrap = styled.section`
  max-width: min(1100px, 96vw);
  margin: 0 auto;
  padding: clamp(1.2rem, 5vw, 2.4rem) 1rem;
`;

const Card = styled.div`
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: clamp(1rem, 3.5vw, 2rem);
  align-items: start;
  background: ${brand.card};
  border: 1px solid ${brand.border};
  border-radius: 18px;
  box-shadow: 0 12px 34px rgba(15, 23, 42, 0.08);
  padding: clamp(1rem, 3.5vw, 1.75rem);
  overflow: hidden;
  min-width: 0;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }

  > * {
    min-width: 0;
  }
`;

const PhotoBlock = styled.div`
  display: grid;
  justify-items: center;
  gap: 0.6rem;
  width: 100%;
  min-width: 0;

  @media (max-width: 860px) {
    order: -1;
  }
`;

const PhotoRing = styled.div`
  --size: clamp(140px, 28vw, 180px);
  width: var(--size);
  height: var(--size);
  border-radius: 50%;
  padding: 4px;
  background: conic-gradient(
    from 180deg,
    ${brand.accent},
    ${brand.primary},
    ${brand.primaryDark},
    ${brand.accent}
  );
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.15);
  display: grid;
  place-items: center;
`;

const Photo = styled.img`
  width: 100%;
  height: 100%;
  max-width: 100%;
  border-radius: 50%;
  object-fit: cover;
  background: #e6eef3;
  display: block;
`;

const Name = styled.h1`
  margin: 0.65rem 0 0;
  font-size: clamp(1.15rem, 3.6vw, 1.6rem);
  color: ${brand.ink};
  font-weight: 800;
  text-align: center;
  letter-spacing: 0.2px;
`;

const Role = styled.p`
  margin: 0.15rem 0 0;
  color: ${brand.sub};
  font-weight: 600;
  text-align: center;
  font-size: clamp(0.9rem, 2.8vw, 1rem);
`;

const Divider = styled.hr`
  height: 1px;
  border: 0;
  background: linear-gradient(90deg, transparent, ${brand.border}, transparent);
  margin: 0.9rem 0 0;
  width: 100%;
`;

const Body = styled.div`
  color: ${brand.body};
  font-size: clamp(0.96rem, 2.1vw, 1.05rem);
  line-height: 1.75;
  text-align: justify;

  p {
    margin: 0 0 0.9rem;
  }
`;

/* ===== Vision Section ===== */
const VisionBox = styled.div`
  margin-top: 1.2rem;
  padding: 0.9rem 1rem;
  background: ${brand.wash};
  border-left: 4px solid ${brand.primary};
  border-radius: 10px;
`;

const VisionTitle = styled.h3`
  margin: 0 0 0.35rem;
  color: ${brand.primaryDark};
  font-size: clamp(1rem, 2.5vw, 1.15rem);
  font-weight: 800;
`;

const VisionText = styled.p`
  margin: 0;
  color: ${brand.body};
  line-height: 1.65;
`;

const ContactBar = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  margin-top: 1.1rem;
  background: ${brand.wash};
  border: 1px dashed ${brand.border};
  border-radius: 12px;
  padding: 0.55rem 0.8rem;
  flex-wrap: wrap;
`;

const ContactText = styled.span`
  color: ${brand.body};
  font-weight: 600;
`;

const Email = styled.a`
  color: ${brand.primaryDark};
  font-weight: 700;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const AboutMe = () => {
  const email = "info@aibuildingskills.com";

  return (
    <>
      <Navbar />
      <PageShell>
        <Main>
          <Band />
          <Wrap>
            <Card>
              <PhotoBlock>
                <PhotoRing>
                  <Photo
                    src={aboutPhoto}
                    alt="Abhishek Shete"
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                </PhotoRing>
                <div>
                  <Name>Abhishek Shete</Name>
                  <Role>Construction Project Manager</Role>
                </div>
              </PhotoBlock>

              <div>
                <Body>
                  <p>
                    I’m Abhishek Shete, a Construction Project Manager with 10
                    years of experience across public, commercial, and life
                    sciences projects. I have managed small to large ground up
                    projects.
                  </p>

                  <p>
                    Over the years, I’ve seen how much time is lost to
                    repetitive documentation and inefficient communication. When
                    I discovered AI and LLM models such as ChatGPT, I realized
                    it could solve many of those challenges.
                  </p>

                  <p>
                    This website is dedicated to helping Construction
                    professionals start using AI without needing to be an
                    expert. I wrote it for engineers and managers who want to
                    work smarter, not harder. The tools and examples on this
                    website are based on actual Construction workflows. My goal
                    is to help you save time, reduce stress, and focus on
                    building great projects.
                  </p>

                  {/* Vision Section */}
                  <VisionBox>
                    <VisionTitle>Vision</VisionTitle>
                    <VisionText>
                      To empower construction professionals to harness the
                      power of AI, transforming the industry for greater
                      efficiency and innovation.
                    </VisionText>
                  </VisionBox>
                </Body>

                <Divider />

                <ContactBar>
                  <ContactText>Contact me at - </ContactText>
                  <Email href={`mailto:${email}`}>{email}</Email>
                </ContactBar>
              </div>
            </Card>
          </Wrap>
        </Main>

        <Footer />
        <PromptRibbon />
      </PageShell>
    </>
  );
};

export default AboutMe;